import { components } from "./apex-client-gen";
import { GoalType, RetirementType } from "./planning-util";
import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * Validates a given name to ensure it contains only alphabetical characters,
 * hyphens ('-'), periods ('.'), or spaces.
 *
 * @param {string} name - The name to be validated.
 * @returns {string | undefined} - Returns an error message if the name is invalid,
 *                                 otherwise returns undefined.
 */
export function validateName(name: string): string | undefined {
  if (!/^[a-zA-Z-. ]+$/.test(name)) {
    return "Name must include only alphabetical characters, '-', or '.'";
  }
  return undefined;
}

/**
 * Validates a phone number in E.164 format.
 *
 * E.164 is an international telephone numbering plan that ensures each device on the PSTN has a globally unique number.
 * The format is a '+' sign followed by the country code and subscriber number, with no spaces or other characters.
 *
 * @param {string} phoneNumber - The phone number to validate.
 * @returns {string | undefined} - Returns an error message if the phone number is invalid, otherwise returns undefined.
 */
export function validateE164PhoneNumber(
  phoneNumber: string,
): string | undefined {
  if (!/^\+[1-9]\d{1,14}$/.test(phoneNumber)) {
    return "Please enter a valid phone number.";
  }
  return undefined;
}

export function normalizeUSPhoneNumber(
  phoneNumber: string,
): string | undefined {
  const parsed = parsePhoneNumberFromString(phoneNumber.trim(), "US");
  if (!parsed?.isValid() || parsed.country !== "US") {
    return undefined;
  }
  return parsed.number;
}

export function validateUSPhoneNumber(phoneNumber: string): string | undefined {
  return normalizeUSPhoneNumber(phoneNumber)
    ? undefined
    : "Please enter a valid phone number.";
}

/**
 * Parses middle names input string and splits by space
 * @param middleNamesString - User input string for middle names
 * @returns Array of middle names (filters names shorter than 2 characters)
 */
export function parseMiddleNames(middleNamesString: string): string[] {
  return middleNamesString.split(" ").filter((name) => name.length > 1); // At least 2 characters
}

/**
 * Validates an array of middle names
 * @param middleNames - Array of middle names to validate
 * @returns Error message or undefined
 */
export function validateMiddleNames(middleNames: string[]): string | undefined {
  for (const name of middleNames) {
    const error = validateName(name);
    if (error) return error;
  }
  return undefined;
}

/**
 * Validates a given date of birth to ensure the user is at least 18 years old
 * and not older than 150 years.
 *
 * @param {Date} dob - The date of birth to be validated.
 * @returns {string | undefined} - Returns an error message if the date of birth is invalid,
 *                                 otherwise returns undefined.
 */
export function validateDateOfBirth(dob: Date): string | undefined {
  let today = new Date();
  const eighteenYearsAgo = new Date(
    new Date().setFullYear(today.getFullYear() - 18),
  );
  today = new Date();
  const oneAndHalfCenturiesAgo = new Date(
    today.setFullYear(today.getFullYear() - 150),
  );
  if (dob > eighteenYearsAgo) {
    return "You must be at least 18 years old to open an account";
  }
  if (dob < oneAndHalfCenturiesAgo) {
    return "Please enter a valid date of birth";
  }
  return undefined;
}

/**
 * Validates the given address object.
 *
 * @param {Address} address - The address object to validate.
 * @returns {boolean} - Returns true if the address is valid, otherwise false.
 */
export function validateAddress(address: Address): string | undefined {
  if (address.country === "US" && !US_STATES.find((v) => v === address.state)) {
    return "Invalid state. Please enter a valid US state in a 2-letter form.";
  }
  if (!address.line1) {
    return "Please enter a non-empty street address";
  }
  if (!address.postalCode) {
    return "Please enter a non-empty postal code.";
  }
  if (address.postalCode.length < 5) {
    return "Please enter a valid postal code. (5 digits or more)";
  }
}

export const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "AS",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "TT",
  "UT",
  "VT",
  "VA",
  "VI",
  "WA",
  "WV",
  "WI",
  "WY",
];

/**
 * Validate occupation name, ensure it does not contain special characters
 * Only allow letters, numbers, spaces, hyphens and periods
 *
 * @param {string} occupation - The occupation name to validate
 * @returns {string | undefined} - Returns an error message if the occupation name is invalid, otherwise returns undefined
 */
export function validateOccupation(occupation: string): string | undefined {
  // allow: letters, numbers, spaces, hyphens, periods, commas, ampersands, and apostrophes
  if (!/^[A-Za-z0-9,&'\- ]+$/.test(occupation)) {
    return "Job title must only contain letters, numbers, spaces, hyphens, periods, commas, ampersands, and apostrophes";
  }
  return undefined;
}

/**
 * Validate employer name, ensure it does not contain special characters
 * Only allow letters, numbers, spaces, hyphens, periods, commas and ampersands
 *
 * @param {string} employer - The employer name to validate
 * @returns {string | undefined} - Returns an error message if the employer name is invalid, otherwise returns undefined
 */
export function validateEmployer(employer: string): string | undefined {
  // allow letters, numbers, spaces, hyphens, periods, commas and ampersands (common in company names)
  if (!/^[a-zA-Z0-9\s.,&-]+$/.test(employer)) {
    return "Employer name must only contain letters, numbers, spaces, hyphens, periods, commas, and ampersands";
  }
  return undefined;
}

export const ISO_3166_1_ALPHA_2_CODES = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
] as const;

export const ISO_3166_1_ALPHA_2_NAME = {
  AD: "Andorra",
  AE: "United Arab Emirates",
  AF: "Afghanistan",
  AG: "Antigua and Barbuda",
  AI: "Anguilla",
  AL: "Albania",
  AM: "Armenia",
  AO: "Angola",
  AQ: "Antarctica",
  AR: "Argentina",
  AS: "American Samoa",
  AT: "Austria",
  AU: "Australia",
  AW: "Aruba",
  AX: "Åland Islands",
  AZ: "Azerbaijan",
  BA: "Bosnia and Herzegovina",
  BB: "Barbados",
  BD: "Bangladesh",
  BE: "Belgium",
  BF: "Burkina Faso",
  BG: "Bulgaria",
  BH: "Bahrain",
  BI: "Burundi",
  BJ: "Benin",
  BL: "Saint Barthélemy",
  BM: "Bermuda",
  BN: "Brunei Darussalam",
  BO: "Bolivia (Plurinational State of)",
  BQ: "Bonaire, Sint Eustatius and Saba",
  BR: "Brazil",
  BS: "Bahamas",
  BT: "Bhutan",
  BV: "Bouvet Island",
  BW: "Botswana",
  BY: "Belarus",
  BZ: "Belize",
  CA: "Canada",
  CC: "Cocos (Keeling) Islands",
  CD: "Congo, Democratic Republic of the",
  CF: "Central African Republic",
  CG: "Congo",
  CH: "Switzerland",
  CI: "Côte d'Ivoire",
  CK: "Cook Islands",
  CL: "Chile",
  CM: "Cameroon",
  CN: "China",
  CO: "Colombia",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cabo Verde",
  CW: "Curaçao",
  CX: "Christmas Island",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DJ: "Djibouti",
  DK: "Denmark",
  DM: "Dominica",
  DO: "Dominican Republic",
  DZ: "Algeria",
  EC: "Ecuador",
  EE: "Estonia",
  EG: "Egypt",
  EH: "Western Sahara",
  ER: "Eritrea",
  ES: "Spain",
  ET: "Ethiopia",
  FI: "Finland",
  FJ: "Fiji",
  FK: "Falkland Islands (Malvinas)",
  FM: "Micronesia (Federated States of)",
  FO: "Faroe Islands",
  FR: "France",
  GA: "Gabon",
  GB: "United Kingdom of Great Britain and Northern Ireland",
  GD: "Grenada",
  GE: "Georgia",
  GF: "French Guiana",
  GG: "Guernsey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Greenland",
  GM: "Gambia",
  GN: "Guinea",
  GP: "Guadeloupe",
  GQ: "Equatorial Guinea",
  GR: "Greece",
  GS: "South Georgia and the South Sandwich Islands",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guinea-Bissau",
  GY: "Guyana",
  HK: "Hong Kong",
  HM: "Heard Island and McDonald Islands",
  HN: "Honduras",
  HR: "Croatia",
  HT: "Haiti",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IM: "Isle of Man",
  IN: "India",
  IO: "British Indian Ocean Territory",
  IQ: "Iraq",
  IR: "Iran (Islamic Republic of)",
  IS: "Iceland",
  IT: "Italy",
  JE: "Jersey",
  JM: "Jamaica",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KG: "Kyrgyzstan",
  KH: "Cambodia",
  KI: "Kiribati",
  KM: "Comoros",
  KN: "Saint Kitts and Nevis",
  KP: "Korea (Democratic People's Republic of)",
  KR: "Korea, Republic of",
  KW: "Kuwait",
  KY: "Cayman Islands",
  KZ: "Kazakhstan",
  LA: "Lao People's Democratic Republic",
  LB: "Lebanon",
  LC: "Saint Lucia",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  LY: "Libya",
  MA: "Morocco",
  MC: "Monaco",
  MD: "Moldova, Republic of",
  ME: "Montenegro",
  MF: "Saint Martin (French part)",
  MG: "Madagascar",
  MH: "Marshall Islands",
  MK: "North Macedonia",
  ML: "Mali",
  MM: "Myanmar",
  MN: "Mongolia",
  MO: "Macao",
  MP: "Northern Mariana Islands",
  MQ: "Martinique",
  MR: "Mauritania",
  MS: "Montserrat",
  MT: "Malta",
  MU: "Mauritius",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexico",
  MY: "Malaysia",
  MZ: "Mozambique",
  NA: "Namibia",
  NC: "New Caledonia",
  NE: "Niger",
  NF: "Norfolk Island",
  NG: "Nigeria",
  NI: "Nicaragua",
  NL: "Netherlands, Kingdom of the",
  NO: "Norway",
  NP: "Nepal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "New Zealand",
  OM: "Oman",
  PA: "Panama",
  PE: "Peru",
  PF: "French Polynesia",
  PG: "Papua New Guinea",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PM: "Saint Pierre and Miquelon",
  PN: "Pitcairn",
  PR: "Puerto Rico",
  PS: "Palestine, State of",
  PT: "Portugal",
  PW: "Palau",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "Réunion",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russian Federation",
  RW: "Rwanda",
  SA: "Saudi Arabia",
  SB: "Solomon Islands",
  SC: "Seychelles",
  SD: "Sudan",
  SE: "Sweden",
  SG: "Singapore",
  SH: "Saint Helena, Ascension and Tristan da Cunha",
  SI: "Slovenia",
  SJ: "Svalbard and Jan Mayen",
  SK: "Slovakia",
  SL: "Sierra Leone",
  SM: "San Marino",
  SN: "Senegal",
  SO: "Somalia",
  SR: "Suriname",
  SS: "South Sudan",
  ST: "Sao Tome and Principe",
  SV: "El Salvador",
  SX: "Sint Maarten (Dutch part)",
  SY: "Syrian Arab Republic",
  SZ: "Eswatini",
  TC: "Turks and Caicos Islands",
  TD: "Chad",
  TF: "French Southern Territories",
  TG: "Togo",
  TH: "Thailand",
  TJ: "Tajikistan",
  TK: "Tokelau",
  TL: "Timor-Leste",
  TM: "Turkmenistan",
  TN: "Tunisia",
  TO: "Tonga",
  TR: "Türkiye",
  TT: "Trinidad and Tobago",
  TV: "Tuvalu",
  TW: "Taiwan, Province of China",
  TZ: "Tanzania, United Republic of",
  UA: "Ukraine",
  UG: "Uganda",
  UM: "United States Minor Outlying Islands",
  US: "United States of America",
  UY: "Uruguay",
  UZ: "Uzbekistan",
  VA: "Holy See",
  VC: "Saint Vincent and the Grenadines",
  VE: "Venezuela (Bolivarian Republic of)",
  VG: "Virgin Islands (British)",
  VI: "Virgin Islands (U.S.)",
  VN: "Viet Nam",
  VU: "Vanuatu",
  WF: "Wallis and Futuna",
  WS: "Samoa",
  YE: "Yemen",
  YT: "Mayotte",
  ZA: "South Africa",
  ZM: "Zambia",
  ZW: "Zimbabwe",
} as const;

export type CountryType = (typeof ISO_3166_1_ALPHA_2_CODES)[number];

function getCountryFlag(code: CountryType) {
  const codePoints = code.split("").map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export function getCountryName(code: CountryType | undefined) {
  return code
    ? getCountryFlag(code) + "  " + ISO_3166_1_ALPHA_2_NAME[code]
    : "Select Country";
}

export type Address = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  country: CountryType;
};

export type NameSuffixType =
  | "NAME_SUFFIX_UNSPECIFIED"
  | "SR"
  | "JR"
  | "III"
  | "IV"
  | "V";

export const EMPLOYMENT_STATUSES = [
  "EMPLOYED",
  "SELF_EMPLOYED",
  "UNEMPLOYED",
  "RETIRED",
  "STUDENT",
] as const;

export type EmploymentStatusType = (typeof EMPLOYMENT_STATUSES)[number];

export const TAX_ID_TYPE = [
  "TAX_ID_TYPE_SSN",
  "TAX_ID_TYPE_ITIN",
  "TAX_ID_TYPE_EIN",
] as const;

export type TaxIdType = (typeof TAX_ID_TYPE)[number];

export const IRS_FORM = [
  // Disable W8 for now
  // "W_8BEN",
  "W_9",
] as const;

export type IRSFormType = (typeof IRS_FORM)[number];

export const USD_RANGE = [
  "USD_RANGE_UNSPECIFIED",
  "UNDER_25K",
  "FROM_25K_TO_50K",
  "FROM_50K_TO_100K",
  "FROM_100K_TO_200K",
  "FROM_200K_TO_300K",
  "FROM_300K_TO_500K",
  "FROM_500K_TO_1M",
  "FROM_1M_TO_5M",
  "OVER_5M",
] as const;

export type UsdRangeType = (typeof USD_RANGE)[number];

export const INVESTMENT_EXPERIENCE = [
  "INVESTMENT_EXPERIENCE_UNSPECIFIED",
  "NONE",
  "LIMITED",
  "GOOD",
  "EXTENSIVE",
] as const;

export type InvestmentExperience = (typeof INVESTMENT_EXPERIENCE)[number];

export const MARITAL_STATUS = [
  "MARITAL_STATUS_UNSPECIFIED",
  "SINGLE",
  "MARRIED",
  "DIVORCED",
  "WIDOWED",
] as const;

export type MaritalStatusType = (typeof MARITAL_STATUS)[number];

export type LargeTrader = {
  id?: string;
  effectiveDate?: {
    day: number;
    month: number;
    year: number;
  };
};

export type TrustedContact = {
  familyName: string;
  givenName: string;
  middleNames: string[];
  phoneNumber: string;
  email: string;
  mailingAddress: Address;
};

/**
 * Validates the entire Trusted Contact object
 * @param contact - Trusted Contact object to validate
 * @returns Object containing validation errors for each field
 */
export function validateTrustedContact(contact: TrustedContact) {
  const errors = {
    givenName: contact.givenName ? validateName(contact.givenName) : undefined,
    familyName: contact.familyName
      ? validateName(contact.familyName)
      : undefined,
    middleNames:
      contact.middleNames.length > 0
        ? validateMiddleNames(contact.middleNames)
        : undefined,
    phoneNumber: contact.phoneNumber
      ? validateE164PhoneNumber(contact.phoneNumber)
      : undefined,
    address: validateAddress(contact.mailingAddress),
  };

  return errors;
}

/**
 * Checks if the Trusted Contact form can be submitted
 * @param contact - Trusted Contact object to check
 * @returns Whether all required fields are filled and valid
 */
export function isValidTrustedContact(contact: TrustedContact): boolean {
  // Check if required fields are filled
  if (!contact.givenName || !contact.familyName || !contact.phoneNumber) {
    return false;
  }

  // Check if all fields pass validation
  const errors = validateTrustedContact(contact);
  const allErrorsUndefined = Object.values(errors).every(
    (error) => error === undefined,
  );

  return allErrorsUndefined;
}

export type ApexUserInformation = {
  riaAgreementsSigned?: Date;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  dateOfBirth?: Date;
  countryOfCitizenship?: CountryType;
  countryOfCitizenship2?: CountryType;
  countryOfBirth?: CountryType;
  taxResidencyCountry?: CountryType;
  taxResidencyRegion?: string;
  personalAddress?: Address;
  suffix?: NameSuffixType;
  phoneNumber?: string;
  employmentStatus?: EmploymentStatusType;
  occupation?: string;
  employer?: string;
  startYear?: string;
  employerAddress?: Address;
  taxId?: string;
  taxIdType: TaxIdType;
  federalTaxClassification?:
    | "INDIV_SOLEPROP_OR_SINGLEMEMBERLLC"
    | "PARTNERSHIP"
    | "C_CORPORATION"
    | "S_CORPORATION"
    | "TRUST_ESTATE"
    | "LLC_TAXED_AS_C_CORP"
    | "LLC_TAXED_AS_S_CORP"
    | "LLC_TAXED_AS_PARTNERSHIP"
    | "OTHER";
  irsFormType?: IRSFormType;
  usITINStatus?: "US_TIN_STATUS_UNSPECIFIED" | "PASSING" | "FAILING";
  annualIncomeRangeUsd?: UsdRangeType;
  totalNetWorthRangeUsd?: UsdRangeType;
  investmentExperience?: InvestmentExperience;
  maritalStatus?: MaritalStatusType;
  isAdvisor?: boolean;
  isInstitutionalCustomer?: boolean;
  isCorrespondentEmployee?: boolean;
  isFinraAffiliated?: boolean;
  isControlPerson?: boolean;
  hasPoliticallyExposedFamily?: boolean;
  hasPoliticallyExposedOrganization?: boolean;
  isLargeTrader?: boolean;
  largeTrader?: LargeTrader;
  controlPersonCompanySymbols?: string[];
  finraAssociatedEntity?: string;
  politicallyExposedFamilyNames?: string;
  politicallyExposedOrganization?: string;
  trustedContacts?: TrustedContact[];
};

function mapAddress(
  // @ts-expect-error add type definition for fetched data after user info state is stablized
  address,
): components["schemas"]["PostalAddressCreate"] {
  // console.log("address", address);
  // console.log("result", {
  //   address_lines: [address?.line1, address?.line2].filter(Boolean),
  //   administrative_area: address.state,
  //   language_code: "en-US",
  //   locality: address.city,
  //   postal_code: address.postalCode,
  //   region_code: address.country,
  // });
  return {
    address_lines: [address?.line1, address?.line2, address?.line3].filter(
      Boolean,
    ),
    administrative_area: address.state,
    language_code: "en-US",
    locality: address.city,
    postal_code: address.postalCode,
    region_code: address.country,
  };
}

export function adaptNaturalPerson(
  userInfo: ApexUserInformation,
): components["schemas"]["CreateNaturalPersonRequest"] {
  if (userInfo.dateOfBirth === undefined) {
    throw new Error("Date of birth is required");
  }
  const citizenshipCountries = [
    userInfo.countryOfCitizenship,
    userInfo.countryOfCitizenship2,
  ].filter((country): country is CountryType => Boolean(country));
  const isControlPerson =
    userInfo.isControlPerson ?? Boolean(userInfo.controlPersonCompanySymbols?.length);
  const hasPoliticallyExposedFamily =
    userInfo.hasPoliticallyExposedFamily ?? Boolean(userInfo.politicallyExposedFamilyNames);
  const hasPoliticallyExposedOrganization =
    userInfo.hasPoliticallyExposedOrganization ??
    Boolean(userInfo.politicallyExposedOrganization);
  const isFinraAffiliated =
    userInfo.isFinraAffiliated ?? Boolean(userInfo.finraAssociatedEntity);

  return {
    accredited_investor: false,
    birth_date: {
      day: userInfo.dateOfBirth.getUTCDate(),
      month: userInfo.dateOfBirth.getUTCMonth() + 1,
      year: userInfo.dateOfBirth.getUTCFullYear(),
    },
    citizenship_countries:
      citizenshipCountries.length > 0 ? citizenshipCountries : [""],
    control_person_company_symbols: isControlPerson
      ? (userInfo.controlPersonCompanySymbols ?? []).join(",")
      : "",
    correspondent_id: "", // TODO: this is a workaround
    employment: {
      employment_status: userInfo.employmentStatus!,
      occupation: userInfo.occupation || "",
      employer: userInfo.employer ?? null,
      employer_address:
        userInfo.employerAddress && mapAddress(userInfo.employerAddress),
      start_year: (userInfo.startYear && parseInt(userInfo.startYear)) || null,
    },
    family_name: userInfo.lastName!,
    given_name: userInfo.firstName!,
    middle_names: userInfo.middleName,
    ...(userInfo.isAdvisor !== undefined && {
      adviser: userInfo.isAdvisor,
    }),
    ...(userInfo.isInstitutionalCustomer !== undefined && {
      institutional_customer: userInfo.isInstitutionalCustomer,
    }),
    ...(userInfo.isCorrespondentEmployee !== undefined && {
      is_correspondent_employee: userInfo.isCorrespondentEmployee,
    }),
    large_trader: userInfo.largeTrader && {
      effective_date: {
        day: userInfo.largeTrader.effectiveDate?.day ?? null,
        month: userInfo.largeTrader.effectiveDate?.month ?? null,
        year: userInfo.largeTrader.effectiveDate?.year ?? null,
      },
      large_trader_id: userInfo.largeTrader.id ?? "",
    },
    marital_status: userInfo.maritalStatus ?? "MARITAL_STATUS_UNSPECIFIED",
    name_suffix: userInfo.suffix ?? "NAME_SUFFIX_UNSPECIFIED",
    non_citizen_residency: {
      residency_status: "US_PERMANENT_RESIDENT",
    },
    personal_address: mapAddress(userInfo.personalAddress),
    politically_exposed_immediate_family_names: hasPoliticallyExposedFamily
      ? (userInfo.politicallyExposedFamilyNames?.split(/, ?/) ?? [])
      : [],
    politically_exposed_organization: hasPoliticallyExposedOrganization
      ? userInfo.politicallyExposedOrganization
      : undefined,
    finra_associated_entity: isFinraAffiliated
      ? userInfo.finraAssociatedEntity
      : undefined,
    subject_to_backup_withholding: false,
    tax_id: userInfo.taxId!,
    tax_id_type: userInfo.taxIdType,
    tax_profile: {
      federal_tax_classification:
        userInfo.federalTaxClassification ??
        "INDIV_SOLEPROP_OR_SINGLEMEMBERLLC",
      irs_form_type: "W_9", // This is the only type supported by APEX
      us_tin_status: userInfo.usITINStatus ?? "US_TIN_STATUS_UNSPECIFIED",
      legal_tax_region_code: "US",
    },
  };
}

type CreateAccountType = components["schemas"]["CreateAccountRequest"];

export function adaptAccount(
  userInfo: ApexUserInformation,
  email: string,
): CreateAccountType {
  const data: CreateAccountType = {
    email_address: email ?? "",
    phone_number: userInfo.phoneNumber ?? "",
    investment_profile: {
      customer_profile: {
        annual_income_range_usd: userInfo.annualIncomeRangeUsd,
        total_net_worth_range_usd: userInfo.totalNetWorthRangeUsd,
      },
    },
    trusted_contacts:
      userInfo.trustedContacts?.map((value) => {
        const data: components["schemas"]["TrustedContactCreate"] = {
          family_name: value.familyName,
          given_name: value.givenName,
          email_address: value.email,
          middle_names: value.middleNames.join(" "),
          mailing_address: {
            address_lines: [
              value.mailingAddress.line1,
              value.mailingAddress.line2,
              value.mailingAddress.line3,
            ].filter(Boolean),
            administrative_area: value.mailingAddress.state,
            postal_code: value.mailingAddress.postalCode,
            locality: value.mailingAddress.city,
            region_code: value.mailingAddress.country,
          },
          phone_number: {
            e164_number: value.phoneNumber,
          },
        };
        return data;
      }) ?? [],
  };
  return data;
}

export interface TimeHorizonCollection {
  "Just Invest"?: number;
  "Major Purchase"?: number;
  "Supplement Income"?: number;
  "High Yield Cash"?: number;
  Retirement?: number;
}

export type Recommendation = {
  "Capital Preservation": number;
  Growth: number;
};

export type InputRow = {
  goal: GoalType;
  recommendation: Recommendation;
  timeHorizon: number;
  retirementTypes?: RetirementType[];
};
